import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import BrandLogo from "../components/BrandLogo";
import { StatusBar, PriorityChip } from "../components/StatusBar";
import { STATUSES, PRIORITIES, STATUS_LABEL } from "../lib/constants";
import { DatePicker } from "../components/DatePicker";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MapPin, Package, Warning, CheckCircle, ShareNetwork, FunnelSimple } from "@phosphor-icons/react";

function isOverdue(item) {
  if (!item?.eta || item.status === "delivered") return false;
  const eta = new Date(item.eta);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return eta < today;
}

const REFRESH_MS = 30_000;

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [, forceTick] = useState(0); // re-render every 5s so "Xs ago" stays fresh

  // Filter state — visible to everyone with the share link
  const [filters, setFilters] = useState({
    query: "",         // matches item name OR code
    contractor: "all",
    priority: "all",
    status: "all",
    etaFrom: "",
    etaTo: "",
  });
  const setFilter = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters({
    query: "", contractor: "all", priority: "all", status: "all", etaFrom: "", etaTo: "",
  });
  const hasActiveFilters =
    filters.query.trim() ||
    filters.contractor !== "all" ||
    filters.priority !== "all" ||
    filters.status !== "all" ||
    filters.etaFrom ||
    filters.etaTo;

  const fetchSheet = useCallback(
    async (isInitial = false) => {
      if (isInitial) setLoading(true);
      else setRefreshing(true);
      try {
        const { data } = await api.get(`/share/${token}`);
        setData(data);
        setLastUpdated(new Date().toISOString());
        setError(null);
      } catch (e) {
        // Only surface the error on the first load — silent fail on background refresh
        if (isInitial) {
          setError(formatApiError(e.response?.data?.detail) || "This link is no longer valid.");
        } else {
          // If a link is revoked mid-session, fall through to the error view
          if (e.response?.status === 404) {
            setError("This share link has been revoked or doesn't exist.");
            setData(null);
          }
        }
      } finally {
        if (isInitial) setLoading(false);
        else setRefreshing(false);
      }
    },
    [token],
  );

  useEffect(() => {
    fetchSheet(true);
  }, [fetchSheet]);

  useEffect(() => {
    if (loading || error) return undefined;
    const id = setInterval(() => fetchSheet(false), REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchSheet, loading, error]);

  useEffect(() => {
    // Keep the "Xs ago" label up to date even between polls.
    const id = setInterval(() => forceTick((n) => n + 1), 5000);
    return () => clearInterval(id);
  }, []);

  // Compute filtered items BEFORE early returns to satisfy rules-of-hooks.
  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    const q = filters.query.trim().toLowerCase();
    return data.items.filter((it) => {
      if (q) {
        const name = (it.name || "").toLowerCase();
        const code = (it.code || "").toLowerCase();
        if (!name.includes(q) && !code.includes(q)) return false;
      }
      if (filters.contractor !== "all" && it.contractor_account_id !== filters.contractor) return false;
      if (filters.priority !== "all" && it.priority !== filters.priority) return false;
      if (filters.status !== "all" && it.status !== filters.status) return false;
      if (filters.etaFrom || filters.etaTo) {
        const eta = it.eta ? it.eta.slice(0, 10) : "";
        if (!eta) return false;
        if (filters.etaFrom && eta < filters.etaFrom) return false;
        if (filters.etaTo && eta > filters.etaTo) return false;
      }
      return true;
    });
  }, [data, filters]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F5F7] flex items-center justify-center">
        <div className="text-sm text-zinc-500">Loading procurement sheet…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#F3F5F7] flex flex-col items-center justify-center px-6 py-10">
        <div className="bg-white rounded-3xl border border-zinc-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-10 text-center max-w-md">
          <div className="w-14 h-14 rounded-2xl bg-amber-50 text-amber-600 mx-auto flex items-center justify-center">
            <Warning size={28} weight="bold" />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[#001E44] mt-5">
            Link unavailable
          </h1>
          <p className="text-sm text-zinc-500 mt-2 leading-relaxed">
            {error || "This share link has been revoked or doesn't exist."}
          </p>
          <Link to="/" className="inline-block mt-6 text-sm font-semibold text-[#007BFF] hover:underline" data-testid="share-back-home">
            Go to liveprocure.com →
          </Link>
        </div>
      </div>
    );
  }

  const { project, contractor, items, contractors: allContractors } = data;
  const isWholeProject = !contractor;
  const contractorById = (allContractors || []).reduce((acc, c) => { acc[c.id] = c; return acc; }, {});

  const counts = filteredItems.reduce((acc, i) => {
    acc.total += 1;
    if (i.status === "delivered") acc.delivered += 1;
    if (isOverdue(i)) acc.overdue += 1;
    return acc;
  }, { total: 0, delivered: 0, overdue: 0 });

  return (
    <div className="min-h-screen bg-[#F3F5F7]">
      <header className="bg-white border-b border-zinc-100">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-3">
          <BrandLogo variant="compact" asLink to="/" />
          <div className="flex items-center gap-3 shrink-0">
            <span
              className="hidden sm:inline-flex items-center gap-1.5 chip bg-emerald-50 text-emerald-700"
              data-testid="share-live-indicator"
              title={`Auto-refreshes every ${REFRESH_MS / 1000}s`}
            >
              <span className="relative inline-flex items-center justify-center">
                <span className="absolute inline-flex h-2 w-2 rounded-full bg-emerald-500 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em]">
                {refreshing ? "Refreshing" : `Live · updated ${timeAgo(lastUpdated)}`}
              </span>
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Shared view · read-only</span>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-4 sm:px-8 py-8 sm:py-12 space-y-8" data-testid="public-share-page">
        <section className="ctrl-card overflow-hidden">
          <div className="relative h-44 sm:h-52 bg-zinc-100">
            {project.thumbnail_url ? (
              <img src={project.thumbnail_url} alt={project.name} className="w-full h-full object-cover" />
            ) : (
              <img src="https://images.unsplash.com/photo-1599707254554-027aeb4deacd?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" alt={project.name} className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#001E44] via-[#001E44]/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <span className="text-[10px] uppercase tracking-[0.2em] text-blue-200">Project</span>
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1" data-testid="share-project-name">
                {project.name}
              </h1>
              <div className="flex items-center gap-2 mt-2 text-zinc-100 text-sm">
                <MapPin size={14} weight="bold" />
                <span>{project.location || "Location not set"}</span>
              </div>
            </div>
          </div>
          <div className="p-5 sm:p-6 flex flex-wrap items-center gap-4 justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                {isWholeProject ? "Procurement schedule for" : "Procurement sheet by"}
              </div>
              <div className="font-display text-xl font-semibold text-[#001E44] mt-1" data-testid="share-contractor-name">
                {isWholeProject ? project.name : contractor.name}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {isWholeProject
                  ? `${(allContractors || []).length} contractor accounts`
                  : (contractor.trade || "General contractor")}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="chip bg-zinc-100 text-zinc-700">{counts.total} items</span>
              <span className="chip bg-emerald-50 text-emerald-700">
                <CheckCircle size={11} weight="bold" /> {counts.delivered} delivered
              </span>
              {counts.overdue > 0 && (
                <span className="chip bg-red-50 text-red-700">
                  <Warning size={11} weight="bold" /> {counts.overdue} overdue
                </span>
              )}
            </div>
          </div>
        </section>

        <section className="space-y-3" data-testid="share-items-section">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <span className="label-eyebrow text-[#007BFF]">Procurement items</span>
              <h2 className="font-display text-2xl font-semibold tracking-tight text-[#001E44] mt-1">Live status</h2>
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs font-semibold uppercase tracking-wider text-[#007BFF] hover:underline"
                data-testid="share-filters-clear"
              >
                Clear filters
              </button>
            )}
          </div>

          {/* --- Filter bar (visible to public share viewers) --- */}
          <div className="ctrl-card p-3 sm:p-4" data-testid="share-filters">
            <div className="flex items-center gap-2 mb-3">
              <FunnelSimple size={14} weight="bold" className="text-zinc-400" />
              <span className="label-eyebrow text-zinc-500">Filters</span>
              <span className="text-[11px] text-zinc-400 ml-auto">Showing {filteredItems.length} of {items.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
              <Input
                value={filters.query}
                onChange={(e) => setFilter("query")(e.target.value)}
                placeholder="Search code or item…"
                className="h-9 text-sm sm:col-span-2"
                data-testid="share-filter-query"
              />
              {isWholeProject && (
                <Select value={filters.contractor} onValueChange={setFilter("contractor")}>
                  <SelectTrigger className="h-9 text-sm" data-testid="share-filter-contractor">
                    <SelectValue placeholder="Contractor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All contractors</SelectItem>
                    {(allContractors || []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={filters.priority} onValueChange={setFilter("priority")}>
                <SelectTrigger className="h-9 text-sm" data-testid="share-filter-priority">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All priorities</SelectItem>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.status} onValueChange={setFilter("status")}>
                <SelectTrigger className="h-9 text-sm" data-testid="share-filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DatePicker
                value={filters.etaFrom}
                onChange={setFilter("etaFrom")}
                placeholder="ETA from"
                className="h-9 text-sm"
                data-testid="share-filter-eta-from"
                compact
              />
              <DatePicker
                value={filters.etaTo}
                onChange={setFilter("etaTo")}
                placeholder="ETA to"
                className="h-9 text-sm"
                data-testid="share-filter-eta-to"
                compact
              />
            </div>
          </div>

          {filteredItems.length === 0 ? (
            <div className="ctrl-card p-12 text-center">
              <Package size={28} weight="bold" className="mx-auto text-zinc-300" />
              <p className="text-sm text-zinc-500 mt-2">
                {items.length === 0
                  ? "No procurement items on this sheet yet."
                  : "No items match the current filters."}
              </p>
            </div>
          ) : (
            <div className="ctrl-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="ctrl-table min-w-[820px]">
                  <thead>
                    <tr>
                      <th className="w-[100px]">Code</th>
                      <th>Item</th>
                      {isWholeProject && <th>Contractor</th>}
                      <th>Priority</th>
                      <th className="w-[260px]">Status</th>
                      <th>ETA</th>
                      <th>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((i) => (
                      <tr key={i.id} data-testid={`share-item-row-${i.id}`}>
                        <td>
                          <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${
                            i.code
                              ? "bg-[#001E44]/5 text-[#001E44] font-semibold"
                              : "text-zinc-300"
                          }`}>
                            {i.code || "—"}
                          </span>
                        </td>
                        <td>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-[#001E44]">{i.name}</span>
                            {isOverdue(i) && (
                              <span className="chip bg-red-50 text-red-700">
                                <Warning size={11} weight="bold" /> Overdue
                              </span>
                            )}
                          </div>
                          {i.description && (
                            <div className="text-xs text-zinc-500 line-clamp-1 mt-0.5">{i.description}</div>
                          )}
                          {i.custom_fields && Object.keys(i.custom_fields).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {Object.entries(i.custom_fields).map(([k, v]) => (
                                <span key={k} className="chip bg-zinc-100 text-zinc-700 text-[10px]">
                                  <span className="font-semibold">{k}:</span> {v}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        {isWholeProject && (
                          <td className="text-sm text-zinc-700">
                            {contractorById[i.contractor_account_id]?.name || "—"}
                          </td>
                        )}
                        <td><PriorityChip priority={i.priority} /></td>
                        <td>
                          <div className="flex flex-col gap-1 max-w-[240px]">
                            <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">{STATUS_LABEL[i.status]}</span>
                            <StatusBar status={i.status} compact />
                          </div>
                        </td>
                        <td className="font-mono text-xs text-zinc-600">
                          {i.eta ? new Date(i.eta).toLocaleDateString() : "—"}
                        </td>
                        <td className="font-mono text-xs">{i.quantity} {i.unit || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        <footer className="pt-4 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-[#001E44] transition-colors"
            data-testid="share-footer-link"
          >
            <ShareNetwork size={14} weight="bold" /> Powered by liveprocure.com
          </Link>
        </footer>
      </main>
    </div>
  );
}
